import { useState } from 'react'
import { Button, Form, Input, Select, DatePicker, Tag, Popconfirm, Table, InputNumber, App } from 'antd'
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { purchaseRequisitionsApi, serviceMastersApi } from '@/features/procurement/api'
import { plantsApi } from '@/features/masters/plants/api'
import type { PurchaseRequisition } from '@/shared/types/procurement'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const PR_TYPES = ['RAW_MATERIAL', 'SPARES', 'SERVICE', 'CAPEX']
const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default', PENDING_APPROVAL: 'orange', APPROVED: 'green',
  REJECTED: 'red', PO_RAISED: 'cyan',
}

export default function PurchaseRequisitionPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [plantFilter, setPlantFilter] = useState<string | undefined>()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()

  const canCreate = usePermission('pr.create')
  const canApprove = usePermission('pr.approve')

  const { data, isLoading } = useQuery({
    queryKey: ['prs', plantFilter, statusFilter],
    queryFn: () => purchaseRequisitionsApi.list({ plant_id: plantFilter, status: statusFilter }),
  })

  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 500 }),
  })
  const { data: services } = useQuery({ queryKey: ['service-masters'], queryFn: () => serviceMastersApi.list() })

  const plantOptions = plantsData?.items.map(p => ({ label: p.name, value: p.id })) ?? []
  const serviceOptions = services?.map(s => ({ label: s.service_name, value: s.id })) ?? []

  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      const payload = {
        ...values,
        required_by: values.required_by ? dayjs(values.required_by as string).format('YYYY-MM-DD') : undefined,
        lines: ((values.lines as any[]) ?? []).map((l: any) => ({
          ...l,
          required_by: l.required_by ? dayjs(l.required_by).format('YYYY-MM-DD') : undefined,
        })),
      }
      return purchaseRequisitionsApi.create(payload)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prs'] })
      message.success('PR created')
      setDrawerOpen(false)
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Create failed'),
  })

  const submitMutation = useMutation({
    mutationFn: (id: string) => purchaseRequisitionsApi.submit(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['prs'] }); message.success('PR submitted for approval') },
    onError: () => message.error('Submit failed'),
  })

  const approveMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      purchaseRequisitionsApi.approve(id, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['prs'] }); message.success('PR approved') },
    onError: () => message.error('Approval failed'),
  })

  const openCreate = () => {
    form.resetFields()
    form.setFieldsValue({ pr_type: 'SPARES', lines: [{ description: '', quantity: 1, uom: 'PCS' }] })
    setDrawerOpen(true)
  }

  const columns = [
    { title: 'PR No', dataIndex: 'pr_no', key: 'pr_no', width: 110 },
    { title: 'Plant', dataIndex: 'plant_name', key: 'plant_name' },
    { title: 'Type', dataIndex: 'pr_type', key: 'pr_type', width: 120 },
    { title: 'Requester', dataIndex: 'requester_name', key: 'requester_name' },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 140,
      render: (v: string) => <Tag color={STATUS_COLORS[v]}>{v}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 200,
      render: (_: unknown, r: PurchaseRequisition) => (
        <div style={{ display: 'flex', gap: 4 }}>
          {r.status === 'DRAFT' && canCreate && (
            <Popconfirm title="Submit for approval?" onConfirm={() => submitMutation.mutate(r.id)}>
              <Button size="small">Submit</Button>
            </Popconfirm>
          )}
          {r.status === 'PENDING_APPROVAL' && canApprove && (
            <>
              <Popconfirm title="Approve this PR?" onConfirm={() => approveMutation.mutate({ id: r.id, status: 'APPROVED' })}>
                <Button size="small" type="primary">Approve</Button>
              </Popconfirm>
              <Popconfirm title="Reject this PR?" onConfirm={() => approveMutation.mutate({ id: r.id, status: 'REJECTED' })}>
                <Button size="small" danger>Reject</Button>
              </Popconfirm>
            </>
          )}
        </div>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Purchase Requisitions"
        subtitle="Raise and track internal purchase requests"
        extra={canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New PR</Button>}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Select allowClear placeholder="Plant" options={plantOptions} onChange={v => setPlantFilter(v)} style={{ width: 180 }} />
        <Select allowClear placeholder="Status"
          options={['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED', 'PO_RAISED'].map(s => ({ label: s, value: s }))}
          onChange={v => setStatusFilter(v)} style={{ width: 170 }}
        />
      </div>

      <DataTable rowKey="id" columns={columns} dataSource={data} loading={isLoading} />

      <FormDrawer
        title="New Purchase Requisition"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={() => form.validateFields().then(v => createMutation.mutate(v))}
        loading={createMutation.isPending}
        form={form}
        width={720}
      >
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select options={plantOptions} />
        </Form.Item>
        <Form.Item name="pr_type" label="PR Type" rules={[{ required: true }]}>
          <Select options={PR_TYPES.map(t => ({ label: t, value: t }))} />
        </Form.Item>
        <Form.Item name="purpose" label="Purpose">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="required_by" label="Required By">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>

        <div style={{ fontWeight: 600, marginBottom: 8, marginTop: 8 }}>Line Items</div>
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name }) => (
                <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                  <Form.Item name={[name, 'description']} style={{ flex: 2, marginBottom: 0 }} rules={[{ required: true }]}>
                    <Input placeholder="Description" />
                  </Form.Item>
                  <Form.Item name={[name, 'quantity']} style={{ width: 80, marginBottom: 0 }} rules={[{ required: true }]}>
                    <InputNumber placeholder="Qty" min={0.001} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name={[name, 'uom']} style={{ width: 80, marginBottom: 0 }} rules={[{ required: true }]}>
                    <Input placeholder="UOM" />
                  </Form.Item>
                  <Form.Item name={[name, 'estimated_rate']} style={{ width: 100, marginBottom: 0 }}>
                    <InputNumber placeholder="Rate" min={0} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name={[name, 'service_id']} style={{ flex: 1, marginBottom: 0 }}>
                    <Select allowClear placeholder="Service (opt)" options={serviceOptions} />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red', marginTop: 8 }} />
                </div>
              ))}
              <Button type="dashed" onClick={() => add({ description: '', quantity: 1, uom: 'PCS' })} icon={<PlusOutlined />} block>
                Add Line
              </Button>
            </>
          )}
        </Form.List>
      </FormDrawer>
    </div>
  )
}
